/**
 * Tiny inline-SVG trend chart (no chart dependency). Two variants:
 *  - 'bars' (default): one bar per point; an optional `overlay` series (e.g.
 *    delivered within total) is drawn inside each bar in the success colour.
 *  - 'line': a polyline with a faint area fill.
 * Each point carries a native <title> tooltip built from `labels`. The whole
 * graphic is one role="img" with an operator-readable aria-label.
 */

export interface SparklineProps {
  readonly values: readonly number[];
  /** Second series drawn over the first, on the same scale. */
  readonly overlay?: readonly number[];
  /** Per-point tooltip labels (e.g. the day). */
  readonly labels?: readonly string[];
  readonly variant?: 'bars' | 'line';
  readonly width?: number;
  readonly height?: number;
  /** Stretch to the container width (bars scale horizontally; height stays). */
  readonly fluid?: boolean;
  readonly ariaLabel: string;
  readonly testId?: string;
  /** Tooltip suffix for the base series / overlay (e.g. 'sipariş', 'teslim'). */
  readonly valueName?: string;
  readonly overlayName?: string;
}

function safe(n: number | undefined): number {
  return typeof n === 'number' && Number.isFinite(n) && n > 0 ? n : 0;
}

export function Sparkline({
  values,
  overlay,
  labels,
  variant = 'bars',
  width = 160,
  height = 36,
  fluid = false,
  ariaLabel,
  testId,
  valueName = '',
  overlayName = '',
}: SparklineProps): JSX.Element {
  const n = values.length;
  const svgWidth: number | string = fluid ? '100%' : width;
  const aspect = fluid ? 'none' : undefined;
  const max = Math.max(1, ...values.map(safe), ...(overlay ?? []).map(safe));
  const pad = 2;
  const innerH = height - pad * 2;
  const scale = (v: number): number => (safe(v) / max) * innerH;
  const tip = (i: number): string => {
    const base = `${labels?.[i] ? `${labels[i]}: ` : ''}${safe(values[i]).toLocaleString('tr-TR')}${valueName ? ` ${valueName}` : ''}`;
    return overlay ? `${base}, ${safe(overlay[i]).toLocaleString('tr-TR')}${overlayName ? ` ${overlayName}` : ''}` : base;
  };

  if (n === 0) {
    return (
      <svg width={svgWidth} height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio={aspect} role="img" aria-label={ariaLabel} data-testid={testId} style={{ display: 'block', maxWidth: '100%' }}>
        <line x1={0} y1={height - pad} x2={width} y2={height - pad} stroke="var(--border-default)" strokeWidth={1} />
      </svg>
    );
  }

  if (variant === 'line') {
    const step = n > 1 ? width / (n - 1) : 0;
    const pts = values.map((v, i) => [n > 1 ? i * step : width / 2, height - pad - scale(v)] as const);
    const line = pts.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
    const area = `0,${height - pad} ${line} ${width},${height - pad}`;
    return (
      <svg
        width={svgWidth}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio={aspect}
        role="img"
        aria-label={ariaLabel}
        data-testid={testId}
        style={{ display: 'block', maxWidth: '100%', overflow: 'visible' }}
      >
        <polygon points={area} fill="var(--brand-500)" fillOpacity={0.1} />
        <polyline points={line} fill="none" stroke="var(--brand-500)" strokeWidth={1.6} strokeLinejoin="round" strokeLinecap="round" />
        {pts.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r={2} fill="var(--brand-500)">
            <title>{tip(i)}</title>
          </circle>
        ))}
      </svg>
    );
  }

  const gap = n > 1 ? Math.min(4, width / n / 3) : 0;
  const barW = Math.max(1, (width - gap * (n - 1)) / n);
  return (
    <svg
      width={svgWidth}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio={aspect}
      role="img"
      aria-label={ariaLabel}
      data-testid={testId}
      style={{ display: 'block', maxWidth: '100%' }}
    >
      {values.map((v, i) => {
        const x = i * (barW + gap);
        const h = safe(v) > 0 ? Math.max(1.5, scale(v)) : 1;
        const oh = overlay && safe(overlay[i]) > 0 ? Math.min(h, Math.max(1.5, scale(overlay[i] ?? 0))) : 0;
        return (
          <g key={i}>
            <title>{tip(i)}</title>
            <rect x={x} y={height - pad - h} width={barW} height={h} rx={1.5} fill="var(--gray-400)" />
            {oh > 0 ? (
              <rect x={x} y={height - pad - oh} width={barW} height={oh} rx={1.5} fill="var(--success-500)" />
            ) : null}
          </g>
        );
      })}
    </svg>
  );
}
